import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getAuth, confirmPasswordReset } from 'firebase/auth';

const schema = z.object({
  password: z.string().min(8, 'At least 8 characters'),
  confirmPassword: z.string(),
}).refine(d => d.password === d.confirmPassword, {
  message: 'Passwords don\'t match',
  path: ['confirmPassword'],
});
type FormData = z.infer<typeof schema>;

export default function ResetPassword() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const [serverError, setServerError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setServerError(null);
    if (!oobCode) {
      setServerError(t('resetPassword.invalidToken'));
      return;
    }
    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, data.password);
      navigate('/login', { state: { passwordReset: true }, replace: true });
    } catch {
      setServerError(t('resetPassword.invalidToken'));
    }
  };

  return (
    <div className="min-h-screen bg-[#081208] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h2 className="text-2xl font-semibold text-white mb-1">{t('resetPassword.title')}</h2>
        <p className="text-white/40 text-sm mb-8">{t('resetPassword.subtitle')}</p>

        {serverError && (
          <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
              {t('resetPassword.password')}
            </label>
            <input
              {...register('password')}
              type="password"
              autoComplete="new-password"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
            />
            {errors.password && (
              <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1.5 tracking-wide">
              {t('resetPassword.confirmPassword')}
            </label>
            <input
              {...register('confirmPassword')}
              type="password"
              autoComplete="new-password"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-400">{errors.confirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2.5 transition-colors"
          >
            {isSubmitting ? '...' : t('resetPassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
